import styled from "@emotion/styled";
import { GrProgressCircle } from "@jnpmedi/grebob";
import { nl2br } from "@newLibs/nl2br";
import { MvnMessage } from "@newLibs/utils";
import { useMyRoleForFolderByIdQuery } from "@newHooks/query/folder/myRoleForFolderById.query";
import { useMyRoleForFolderFileByIdQuery } from "@newHooks/query/folder/myRoleForFolderFileById.query";
import { COMMON_CODE } from "@maven-msg/code/common";
import { DOCS_FOLDER_CODE } from "@maven-msg/code/docs";
import { RestError } from "@maven-rest/common/HttpRequest";
import { ReadableFolderSharingRole } from "@maven-rest/docs/FolderSharing.conv";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";

export function SharingRoleGuardView({
  myRole,
  isAuthorized,
  error,
  isLoading,
  children,
}: {
  myRole: FolderSharingRole | null;
  isAuthorized?: boolean;
  error?: RestError;
  isLoading: boolean;
  children: (isAuthorized: boolean | undefined, myRole: FolderSharingRole | null) => React.ReactNode;
}) {
  if (isLoading) {
    return (
      <StyledGuardMessage>
        <GrProgressCircle size={20} variant="indeterminate" />
      </StyledGuardMessage>
    );
  }

  if (isAuthorized === false) {
    return (
      <StyledGuardMessage>
        {nl2br(
          MvnMessage(DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED, {
            type: ReadableFolderSharingRole({ role: myRole || FolderSharingRole.None }),
          })
        )}
      </StyledGuardMessage>
    );
  } else if (error) {
    return (
      <StyledGuardMessage isError>
        {nl2br(MvnMessage(COMMON_CODE.UNHANDLED_EXCEPTION_WITH_MSG, { message: error.responseMessage }))}
      </StyledGuardMessage>
    );
  }

  if (!myRole) {
    return (
      <StyledGuardMessage>
        <GrProgressCircle size={20} variant="indeterminate" />
      </StyledGuardMessage>
    );
  } else if (myRole && !isAuthorized) {
    return (
      <StyledGuardMessage>
        {nl2br(MvnMessage(DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED, { type: String(myRole) }))}
      </StyledGuardMessage>
    );
  }

  return <>{children(isAuthorized, myRole || null)}</>;
}

export function SharingRoleGuard({
  folderId,
  fileId,
  role,
  children,
}: {
  folderId?: bigint;
  fileId?: bigint;
  role?: FolderSharingRole;
  children: (isAuthorized: boolean | undefined, myRole: FolderSharingRole | null) => React.ReactChild;
}) {
  const render = (myRole?: FolderSharingRole, errorResp?: RestError) => {
    if (role) {
      if (errorResp?.responseCode === DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED.code) {
        return (
          <StyledGuardMessage>
            {nl2br(MvnMessage(DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED, errorResp?.response?.data?.binds))}
          </StyledGuardMessage>
        );
      } else if (errorResp) {
        return (
          <StyledGuardMessage>
            {nl2br(MvnMessage(COMMON_CODE.UNHANDLED_EXCEPTION_WITH_MSG, { message: errorResp.responseMessage }))}
          </StyledGuardMessage>
        );
      }
    }

    if (!myRole && !errorResp) {
      return (
        <StyledGuardMessage>
          <GrProgressCircle size={20} variant="indeterminate" />
        </StyledGuardMessage>
      );
    }

    const isAuthorized = ((sharingRoles) => {
      if (!role || !myRole) return undefined;
      return sharingRoles.indexOf(myRole) >= sharingRoles.indexOf(role);
    })(Object.values(FolderSharingRole));

    if (isAuthorized === false) {
      return (
        <StyledGuardMessage>
          {nl2br(MvnMessage(DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED, { type: String(role) }))}
        </StyledGuardMessage>
      );
    }

    return <>{children(isAuthorized, myRole || null)}</>;
  };

  const { data: myRoleForFolderFileByIdData, error: myRoleForFolderFileByIdError } = useMyRoleForFolderFileByIdQuery(
    { fileId: fileId || BigInt(-1) },
    {
      enabled: !!fileId,
    }
  );
  const { data: myRoleForFolderByIdData, error: myRoleForFolderByIdError } = useMyRoleForFolderByIdQuery(
    { folderId: folderId || BigInt(-1) },
    { enabled: !!folderId && !fileId }
  );

  return (
    <>
      {fileId &&
        (myRoleForFolderFileByIdError
          ? render(undefined, myRoleForFolderFileByIdError || undefined)
          : render(myRoleForFolderFileByIdData?.role))}

      {folderId &&
        !fileId &&
        (myRoleForFolderByIdError ? render(undefined, myRoleForFolderByIdError || undefined) : render(myRoleForFolderByIdData?.role))}
    </>
  );
}

export const StyledGuardMessage = styled.div<{ isError?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  text-align: center;
  opacity: 1;

  ${({ isError }) =>
    isError
      ? `
        &:before {
          content: "warning";
        }
      `
      : ""};
`;
